/**
 * SIL-1348 - the lot-contents card on the buyer listing detail (spec section
 * 4.2, handoff section 2 item 2).
 *
 * Two tiers, two different sources, both already decided by the server:
 *
 *  - `members` is the grouped, tier-independent summary (asset class, condition,
 *    title, node count) from `listing_lot_contents_public()`. It carries no id,
 *    fingerprint or serial, so EVERY tier sees it.
 *  - `lotMembers` is the per-node detail (fingerprint, chassis serial, verdict,
 *    custody) from `listing_lot_members_for_buyer()`. It is `null` for an
 *    unverified non-owner — the client does not filter it down, it is simply
 *    not sent — and this component renders the locked note in its place.
 *
 * ## The SIL-1349 seam
 *
 * SIL-1349 builds the node Passport drawer and opens it from a row click and
 * from the "Passport" button in this table. `onOpenNodePassport` is that seam:
 * pass it and both affordances call it with the row's `LotMemberForBuyer`; omit
 * it (today's behaviour) and the Passport cell stays a `Link` to the full
 * passport route. SIL-1349 therefore wires one prop and deletes nothing —
 * no restructuring of this table, and the link-out remains the drawer footer's
 * "Open full Passport" target.
 */
import type { ReactNode } from 'react';
import type { LotMemberForBuyer } from '../../dto/marketplace.js';
import type { ListingLotMember } from '../../dto/listing.js';
import { Card } from '../Card.js';
import { cn } from '../utils.js';
import { verificationVerdictFamily } from '../../verification/presentation.js';
import { formatDate } from '../format.js';
import { ACTIVE_VERIFICATION_VERDICTS } from './copy/activeVerification';
import {
  LOT_CONTENTS_EMPTY,
  LOT_CONTENTS_EYEBROW,
  LOT_CONTENTS_LOCKED,
  LOT_CONTENTS_TITLE,
  LOT_TABLE_COLUMNS,
  LOT_TABLE_PASSPORT_ACTION,
  lotContentsSubtitle,
  lotCustodyEvents,
} from './copy/listingDetail';

const VERDICT_TONE: Record<'success' | 'warn' | 'error', string> = {
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warn: 'bg-amber-50 text-amber-800 ring-amber-200',
  error: 'bg-red-50 text-red-700 ring-red-200',
};

/**
 * The verdict badge. Labels and tones come from the reviewed explainer copy
 * module, so the badge a buyer reads on a row and the verdict table they open
 * from "What is tested" cannot disagree.
 *
 * ## The prop is a RAW verdict, and it is normalised here (SIL-1366)
 *
 * `lotMembers[].verdict` is the raw `verification_run.verdict` — a fine-grained
 * classification like `fail:thermal_throttle`, which matches NO id in
 * `ACTIVE_VERIFICATION_VERDICTS`. Looking the raw string up directly missed
 * every failure and fell through to the em dash below, so a buyer read a FAIL
 * as "no run in the window" (the confusion the third AV state exists to
 * prevent, pointing the wrong way — failed hardware looked merely unverified).
 * `verificationVerdictFamily` collapses the raw value onto a family id first.
 *
 * A null FAMILY is the NEUTRAL third state at row level — a node with no run in
 * the window, or a run that could not complete. It renders an em dash, never a
 * failure tone (SIL-1345).
 *
 * Exported for the SIL-1349 node Passport drawer, so the badge a buyer reads on
 * a row and the one in the drawer they opened FROM that row share a component
 * and a tone palette. The drawer passes a verdict id already normalised onto
 * this copy table (see `nodeVerdictId` in `NodePassportDrawer.tsx`), and the
 * normalisation above is a FIXED POINT on such an id — so the prop can stay
 * `string | null` and both callers are safe.
 */
export function VerdictBadge({ verdict }: { verdict: string | null }) {
  const family = verificationVerdictFamily(verdict);
  const copy = ACTIVE_VERIFICATION_VERDICTS.find((v) => v.id === family);
  if (copy === undefined) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        VERDICT_TONE[copy.tone],
      )}
    >
      {copy.label}
    </span>
  );
}

function Th({ children, className }: { children: string; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground',
        className,
      )}
    >
      {children}
    </th>
  );
}

export interface LotContentsTableProps {
  /** Grouped summary — all tiers. */
  members: ListingLotMember[];
  /** Per-node detail — verified buyers and the owner only; null otherwise. */
  lotMembers: LotMemberForBuyer[] | null;
  nodeCount: number;
  /** GPUs across the lot, when the SKU carries a per-node GPU count. */
  gpuCount: number | null;
  /** SIL-1349's seam — see this module's header. */
  onOpenNodePassport?: (member: LotMemberForBuyer) => void;
  /**
   * The Passport cell when no drawer is wired. The route link is the host app's
   * concern (this module is shared with the ops "View as buyer" preview, which
   * has no customer passport route to link to), so the host supplies it.
   * Absent = the cell is empty.
   */
  renderPassportLink?: (member: LotMemberForBuyer) => ReactNode;
}

export function LotContentsTable({
  members,
  lotMembers,
  nodeCount,
  gpuCount,
  onOpenNodePassport,
  renderPassportLink,
}: LotContentsTableProps) {
  const rowAction = onOpenNodePassport;

  return (
    <Card as="section">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {LOT_CONTENTS_EYEBROW}
      </p>
      <h2 className="mt-1 text-[15px] font-semibold text-foreground">{LOT_CONTENTS_TITLE}</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {lotContentsSubtitle(nodeCount, gpuCount)}
      </p>

      {members.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">{LOT_CONTENTS_EMPTY}</p>
      ) : (
        <ul className="mt-4 divide-y divide-border rounded-lg border border-border">
          {members.map((m, i) => (
            <li key={`${m.assetClassName ?? ''}-${m.conditionBand ?? ''}-${i}`} className="px-3 py-2.5">
              <p className="truncate text-sm font-medium text-foreground">{m.displayTitle ?? '—'}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[m.assetClassName, m.conditionBand].filter(Boolean).join(' · ') || '—'}
                {' · '}
                {m.nodeCount} {m.nodeCount === 1 ? 'node' : 'nodes'}
              </p>
            </li>
          ))}
        </ul>
      )}

      {lotMembers === null ? (
        <p className="mt-4 rounded-lg border border-dashed border-border px-3 py-3 text-xs text-muted-foreground">
          {LOT_CONTENTS_LOCKED}
        </p>
      ) : lotMembers.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full text-sm">
            <thead className="border-b border-border bg-muted">
              <tr>
                <Th>{LOT_TABLE_COLUMNS.node}</Th>
                <Th>{LOT_TABLE_COLUMNS.chassisSerial}</Th>
                <Th>{LOT_TABLE_COLUMNS.activeVerification}</Th>
                <Th>{LOT_TABLE_COLUMNS.runDate}</Th>
                <Th>{LOT_TABLE_COLUMNS.custody}</Th>
                <Th className="text-right">{LOT_TABLE_COLUMNS.passport}</Th>
              </tr>
            </thead>
            <tbody>
              {lotMembers.map((m, i) => (
                <tr
                  key={m.assetId}
                  onClick={rowAction === undefined ? undefined : () => rowAction(m)}
                  className={cn(
                    'border-b border-border last:border-b-0',
                    rowAction !== undefined && 'cursor-pointer hover:bg-muted/50',
                  )}
                >
                  <td className="px-3 py-2 align-middle text-sm text-foreground">{i + 1}</td>
                  <td className="px-3 py-2 align-middle font-mono text-xs text-foreground">
                    {m.chassisSerial ?? m.fingerprint}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <VerdictBadge verdict={m.verdict} />
                  </td>
                  <td className="px-3 py-2 align-middle text-sm text-muted-foreground">
                    {formatDate(m.runAt)}
                  </td>
                  <td className="px-3 py-2 align-middle text-sm text-muted-foreground">
                    {lotCustodyEvents(m.custodyEvents)}
                  </td>
                  <td className="px-3 py-2 align-middle text-right">
                    {rowAction === undefined ? (
                      (renderPassportLink?.(m) ?? null)
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          // The row is clickable too — without this the drawer
                          // would be asked to open twice for one click.
                          e.stopPropagation();
                          rowAction(m);
                        }}
                        className="text-xs font-medium text-brand-600 hover:underline"
                      >
                        {LOT_TABLE_PASSPORT_ACTION}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}
